import dayjs from 'dayjs'
import { makeAutoObservable, runInAction } from 'mobx'

import type { Notify } from '@/Ship/Contracts/Notify'
import { pluralizeUk } from '@/Ship/Helpers/pluralize'
import { CreateShiftBatchTask } from '../Tasks/CreateShiftBatchTask'

/** Найдовший період, днів (включно з обома датами) — як CreateShiftBatchAction::MAX_DAYS */
export const MAX_PERIOD_DAYS = 5

/** Найбільше рядків в одному запиті — як CreateShiftBatchAction::MAX_ROWS */
export const MAX_ROWS = 50

/** Рядок таблиці: водій і його ТЗ (IRI) */
export interface ShiftRow {
    id: number
    driver: string | null
    vehicle: string | null
}

const DATE = 'YYYY-MM-DD'

/** Назви полів для помилок бекенду, що стосуються всього вікна */
const FIELD_LABELS: Record<string, string> = {
    dateFrom: 'Дата від',
    dateTo: 'Дата до',
    cluster: 'Кластер',
    region: 'Регіон',
}

/**
 * Стан вікна множинного створення змін (/shifts → «Додати кілька»): період до 5 днів,
 * кластер/регіон і рядки «водій + ТЗ». Кожен рядок отримує зміну на кожну дату періоду.
 * Помилки бекенду (422, напр. зміна вже існує) показуються списком із номерами рядків.
 */
export class MultiShiftDrawerStore {
    open = false
    submitting = false

    dateFrom: string | null = null
    dateTo: string | null = null
    cluster: string | null = null
    region: string | null = null

    rows: ShiftRow[] = [{ id: 1, driver: null, vehicle: null }]
    private nextRowId = 2

    /** Помилки останнього збереження (зміни вже існують тощо) */
    errors: string[] = []

    constructor() {
        makeAutoObservable(this)
    }

    openDrawer(): void {
        this.reset()
        this.open = true
    }

    closeDrawer(): void {
        this.open = false
    }

    reset(): void {
        this.dateFrom = null
        this.dateTo = null
        this.cluster = null
        this.region = null
        this.rows = [{ id: 1, driver: null, vehicle: null }]
        this.nextRowId = 2
        this.errors = []
    }

    /** «Дата від» тягне за собою «Дату до», якщо та порожня або виходить за 5 днів */
    setDateFrom(value: string | null): void {
        this.errors = []
        this.dateFrom = value
        if (value && (!this.dateTo || !isWithinPeriod(value, this.dateTo))) {
            this.dateTo = value
        }
    }

    setDateTo(value: string | null): void {
        this.errors = []
        this.dateTo = value
    }

    /** Регіон обирається серед регіонів кластера — зміна кластера його скидає */
    setCluster(value: string | null): void {
        this.errors = []
        if (value !== this.cluster) this.region = null
        this.cluster = value
    }

    setRegion(value: string | null): void {
        this.errors = []
        this.region = value
    }

    /** ТЗ обирається серед ТЗ перевізника водія — зміна водія його скидає */
    setRowDriver(id: number, value: string | null): void {
        this.errors = []
        this.rows = this.rows.map((r) =>
            r.id === id ? { ...r, driver: value, vehicle: r.driver === value ? r.vehicle : null } : r
        )
    }

    setRowVehicle(id: number, value: string | null): void {
        this.errors = []
        this.rows = this.rows.map((r) => (r.id === id ? { ...r, vehicle: value } : r))
    }

    addRow(): void {
        if (this.rows.length >= MAX_ROWS) return
        this.rows = [...this.rows, { id: this.nextRowId++, driver: null, vehicle: null }]
    }

    removeRow(id: number): void {
        if (this.rows.length <= 1) return
        // Номери рядків у списку помилок після видалення вже не відповідають таблиці
        this.errors = []
        this.rows = this.rows.filter((r) => r.id !== id)
    }

    /** Кількість днів періоду; 0 — період не обрано або він некоректний */
    get days(): number {
        if (!this.dateFrom || !this.dateTo) return 0
        const days = dayjs(this.dateTo, DATE).diff(dayjs(this.dateFrom, DATE), 'day') + 1
        return days >= 1 && days <= MAX_PERIOD_DAYS ? days : 0
    }

    /** Рядки, у яких щось обрано (повністю порожні не відправляються) */
    get filledRows(): ShiftRow[] {
        return this.rows.filter((r) => !!r.driver || !!r.vehicle)
    }

    /** Скільки змін буде створено: заповнені рядки × дні */
    get shiftsCount(): number {
        return this.filledRows.filter((r) => r.driver && r.vehicle).length * this.days
    }

    get isDirty(): boolean {
        return !!(this.dateFrom || this.dateTo || this.cluster || this.region || this.filledRows.length > 0)
    }

    /** Перша причина, чому зберегти ще не можна; null — можна */
    get validationError(): string | null {
        if (!this.dateFrom || !this.dateTo) return 'Оберіть період'
        if (this.days === 0) return `Період — від 1 до ${MAX_PERIOD_DAYS} днів`
        if (!this.cluster) return 'Оберіть кластер'
        if (!this.region) return 'Оберіть регіон'
        if (this.filledRows.length === 0) return 'Додайте щонайменше одного водія'
        // Номер першої появи пари «водій + ТЗ» — щоб повтор назвати номерами з таблиці
        const seen = new Map<string, number>()
        for (const row of this.filledRows) {
            const number = this.rows.indexOf(row) + 1
            if (!row.driver) return `Рядок ${number}: оберіть водія`
            if (!row.vehicle) return `Рядок ${number}: оберіть транспортний засіб`
            const key = `${row.driver}|${row.vehicle}`
            const first = seen.get(key)
            if (first) return `Рядок ${number}: такі водій і ТЗ уже є в рядку ${first}`
            seen.set(key, number)
        }
        return null
    }

    async submit(notify: Notify, onSuccess?: () => void): Promise<void> {
        const error = this.validationError
        if (error) {
            notify.error(error)
            return
        }

        // Номери рядків у відповіді бекенду — індекси серед відправлених, а не серед показаних
        const sent = this.filledRows
        const rowNumbers = sent.map((row) => this.rows.indexOf(row) + 1)

        this.submitting = true
        this.errors = []
        try {
            const created = await CreateShiftBatchTask({
                dateFrom: this.dateFrom!,
                dateTo: this.dateTo!,
                cluster: this.cluster!,
                region: this.region!,
                rows: sent.map((r) => ({ driver: r.driver!, vehicle: r.vehicle! })),
            })
            const count = Array.isArray(created) ? created.length : this.shiftsCount
            notify.success(`Створено ${count} ${pluralizeUk(count, ['зміну', 'зміни', 'змін'])}`)
            runInAction(() => {
                this.open = false
                this.reset()
            })
            onSuccess?.()
        } catch (e: any) {
            const errors = violationMessages(e?.response?.violations, rowNumbers)
            runInAction(() => {
                this.errors = errors
            })
            notify.error(
                errors.length > 0
                    ? 'Зміни не створено — виправте помилки у вікні'
                    : e?.response?.message || e?.message || 'Не вдалося створити зміни'
            )
        } finally {
            runInAction(() => {
                this.submitting = false
            })
        }
    }
}

/** Чи вкладається період «від — до» у 5 днів (включно) */
export const isWithinPeriod = (from: string, to: string): boolean => {
    const days = dayjs(to, DATE).diff(dayjs(from, DATE), 'day')
    return days >= 0 && days < MAX_PERIOD_DAYS
}

/**
 * Порушення бекенду → рядки для показу: rows[i]… — «Рядок N: …» (N — номер у таблиці),
 * спільні поля — «Кластер: …».
 */
export const violationMessages = (violations: unknown, rowNumbers: number[]): string[] => {
    if (!Array.isArray(violations)) return []
    return violations
        .filter((v: any) => typeof v?.message === 'string')
        .map((v: any) => {
            const field = String(v.field ?? v.propertyPath ?? '')
            const row = /^rows\[(\d+)\]/.exec(field)
            if (row) return `Рядок ${rowNumbers[Number(row[1])] ?? Number(row[1]) + 1}: ${v.message}`
            const label = FIELD_LABELS[field]
            return label ? `${label}: ${v.message}` : v.message
        })
}
